import { Check, Loader2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FIELD_LABELS } from "../commissionEditQueue.constants";
import { formatDateTime, formatValue } from "../commissionEditQueue-formatters";
import type { RequestContext } from "../commissionEditQueue.types";
import { CommissionEditStatusBadge } from "./CommissionEditStatusBadge";
import { DetailStat } from "./CommissionEditDetailPrimitives";

interface ChangeRow {
  key: string;
  label: string;
  currentValue: unknown;
  newValue: unknown;
}

function fieldLabel(key: string): string {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

function buildChangeRows(context: RequestContext): ChangeRow[] {
  const { request, transaction, commissionType } = context;
  const transactionRecord = transaction as unknown as Record<string, unknown> | undefined;
  const rows: ChangeRow[] = [];

  for (const [key, newValue] of Object.entries(request.proposedChanges)) {
    if (key === "details" && newValue && typeof newValue === "object") {
      const currentDetails =
        transaction?.details && typeof transaction.details === "object"
          ? (transaction.details as Record<string, unknown>)
          : {};
      const nextDetails = newValue as Record<string, unknown>;
      const detailKeys = [...new Set([...Object.keys(currentDetails), ...Object.keys(nextDetails)])];

      for (const detailKey of detailKeys) {
        if (sameJson(currentDetails[detailKey], nextDetails[detailKey])) continue;
        rows.push({
          key: `details.${detailKey}`,
          label: fieldLabel(detailKey),
          currentValue: currentDetails[detailKey],
          newValue: nextDetails[detailKey],
        });
      }
      continue;
    }

    rows.push({
      key,
      label: FIELD_LABELS[key] ?? fieldLabel(key),
      currentValue: transactionRecord?.[key],
      newValue,
    });
  }

  if (rows.length > 0) return rows;

  const fieldSchema = commissionType?.fieldSchema ?? {};
  return Object.keys(fieldSchema).map((key) => ({
    key: `details.${key}`,
    label: fieldLabel(key),
    currentValue: undefined,
    newValue: undefined,
  }));
}

interface CommissionEditRequestDetailDialogProps {
  context: RequestContext | null;
  busyId: number | null;
  rejectingId: number | null;
  rejectNote: string;
  onOpenChange: (open: boolean) => void;
  onApprove: (requestId: number) => void;
  onReject: (requestId: number) => void;
  onStartReject: (requestId: number | null) => void;
  onRejectNote: (note: string) => void;
}

export function CommissionEditRequestDetailDialog({
  context,
  busyId,
  rejectingId,
  rejectNote,
  onOpenChange,
  onApprove,
  onReject,
  onStartReject,
  onRejectNote,
}: CommissionEditRequestDetailDialogProps) {
  const request = context?.request;
  const transaction = context?.transaction;
  const changeRows = context ? buildChangeRows(context) : [];

  return (
    <Dialog open={Boolean(context)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-3xl">
        {context && request && (
          <>
            <DialogHeader>
              <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <DialogTitle className="text-[17px] font-bold text-foreground">
                    Demande #{request.id}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                    {context.typeLabel ?? transaction?.type ?? "Commission"} #
                    {request.commissionTransactionId} - demandée par{" "}
                    {context.requester?.fullName ?? `Agent #${request.requestedBy}`}
                  </DialogDescription>
                </div>
                <CommissionEditStatusBadge status={request.status} />
              </div>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-3">
              <DetailStat label="Demandée" value={formatDateTime(request.createdAt)} />
              <DetailStat
                label="Révisée"
                value={
                  request.status === "pending" ? "Non révisée" : formatDateTime(request.reviewedAt)
                }
              />
              <DetailStat
                label="Réviseur"
                value={
                  request.status === "pending"
                    ? "Non assigné"
                    : (context.reviewer?.fullName ?? `Utilisateur #${request.reviewedBy}`)
                }
              />
            </div>

            <div className="rounded-lg border border-border bg-surface-muted px-4 py-3">
              <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                Raison
              </p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-body">{request.reason}</p>
            </div>

            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-left">
                <thead className="bg-surface-muted">
                  <tr>
                    <th className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Champ
                    </th>
                    <th className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Valeur avant révision
                    </th>
                    <th className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Proposition
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {changeRows.map(({ key, label, currentValue, newValue }) => {
                    return (
                      <tr key={key} className="border-t border-border">
                        <td className="px-4 py-2 text-[12px] text-body">
                          {label}
                        </td>
                        <td className="px-4 py-2 text-[12px] text-muted-foreground">
                          {formatValue(key, currentValue)}
                        </td>
                        <td className="px-4 py-2 text-[12px] font-medium text-success-text">
                          {formatValue(key, newValue)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {request.reviewNote && (
              <div className="rounded-lg border border-danger-border bg-danger-bg px-4 py-3">
                <p className="text-[10.5px] font-semibold uppercase tracking-wide text-danger-text">
                  Note de révision
                </p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-danger-text">
                  {request.reviewNote}
                </p>
              </div>
            )}

            {request.status === "pending" && rejectingId === request.id && (
              <div className="rounded-lg border border-border bg-surface-muted px-4 py-3">
                <Input
                  placeholder="Motif du refus (optionnel)"
                  value={rejectNote}
                  onChange={(event) => onRejectNote(event.target.value)}
                />
              </div>
            )}

            <DialogFooter>
              {request.status === "pending" ? (
                rejectingId === request.id ? (
                  <>
                    <Button variant="secondary" onClick={() => onStartReject(null)}>
                      Annuler
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => onReject(request.id)}
                      disabled={busyId === request.id}
                    >
                      Confirmer le refus
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => onStartReject(request.id)}
                      disabled={busyId === request.id}
                    >
                      <X size={13} /> Refuser
                    </Button>
                    <Button onClick={() => onApprove(request.id)} disabled={busyId === request.id}>
                      {busyId === request.id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Check size={13} />
                      )}
                      Approuver
                    </Button>
                  </>
                )
              ) : (
                <Button variant="secondary" onClick={() => onOpenChange(false)}>
                  Fermer
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
